/**
 * RWBR — PROBLEM results, and the DRIVER FINDER charts they depend on.
 *
 * RULE (rulebook p.iv, verbatim):
 *   "When PROBLEM or RARE results occur, you'll roll the red and white dice,
 *    and check the special PROBLEM RESULTS or RARE RESULTS charts, printed on a
 *    separate game board.  On PROBLEM results, you'll roll on the DRIVER FINDER
 *    chart first, to determine which driver has the problem, and then the
 *    PROBLEM FINDER chart, to determine the specific problem. ...  NOTE that on
 *    PROBLEM and RARE results, the race LEADER is included in the DRIVER FINDER
 *    chart as part of the TOP group."
 *
 * RULE (rulebook p.iv): "Often, a PROBLEM result will call for a 'repair roll.'
 *   NOTE there are separate REPAIR ROLL charts for TROUBLE and PROBLEM results."
 *   Row 15 of the PROBLEM FINDER is the one row that sends you to the TROUBLE
 *   table instead; charts.json encodes that per row.
 *
 * RULE (rulebook p.iv): "A problem will trigger the need to FILL A GAP and if a
 *   YELLOW flag resulted, one YELLOW FLAG turn."
 *
 * RULE (rulebook p.xiv): "a driver CANNOT use a performance chip to avoid a
 *   PROBLEM." This module never reads a chip balance for avoidance — the only
 *   thing it does with chips is FORFEIT them (p.xv) when the car is sent to the
 *   BOTTOM group or out of the race.
 *
 * RULE (PROBLEM RESULTS board, printed procedure): many rows carry a pit-crew
 *   escape — "(Pit Crew A or B, problem does not occur)". That test is applied
 *   BEFORE any other effect on the row, including before an ODD PROBLEM
 *   redirect (rows 33 and 55 are ODD PROBLEM rows that carry an escape; row 11
 *   is an ODD PROBLEM row that does not, so it always cascades).
 *
 * Dice notation: the DRIVER FINDER and PROBLEM FINDER are 11-66 TWO-DIGIT reads
 * (tens = red die, ones = white die), never sums. The repair roll is a 2d6 SUM.
 * See dice.ts — the two notations must not be conflated.
 */

import type { PitCrewGrade } from './types.ts';
import { groupOf } from './types.ts';
import type { Dice } from './dice.ts';
import {
  CHARTS,
  CITE,
  resolveRepair,
  type ChartEffect,
  type ChartEntry,
  type DiceRecord,
  type LedgerEvent,
  type Modifier,
  type Placement,
  type PitCrewException,
  type PositionDelta,
  type RepairResult,
  type RepairTable,
  type TrackAdjustment,
} from './repair.ts';
import type { TroubleCar, TroubleField, TroubleGroup } from './trouble.ts';

/** D-EZ-87 — which era's machinery the field is running. Re-exported by houseRules.ts. */
export type Era = 'modern' | '1990s';
export const CITE_ERA =
  'House rule D-EZ-87 — era dial, modern: a failed PROBLEM repair goes to the garage and returns laps down';

// ---------------------------------------------------------------------------
// DRIVER FINDER (p.iv)
// ---------------------------------------------------------------------------

const BOARD_B_MIDDLE = (
  CHARTS['board-b-middle-group-layout'] as unknown as {
    entries: Record<string, { odd: number; even: number; text: string }>;
  }
).entries;

/** Board A: TOP group driver #n sits in (n + 1)th place. LEADER is 1st. */
/** EZRA EN — Engineer. A d6 of 4 or better works the problem out. */
export const EN_AVOIDS_FROM = 4;
export const CITE_EN_TRAIT =
  'EZRA-S2 §4.0 — "An Engineer can identify potential problems and in some cases avoid or work ' +
  'around them. Any time this driver is faced with a problem roll a d6. 1-3 the problem exists ' +
  'handle per normal rules, 4-6 the problem is avoided."';

export function topGroupPosition(driver: number | 'LEADER'): number {
  if (driver === 'LEADER') return 1;
  if (!Number.isInteger(driver) || driver < 1 || driver > 6) {
    throw new RangeError(`TOP group driver index must be 1-6, got ${driver}`);
  }
  return driver + 1;
}

/** Board B: six boxes of two. Box b, ODD = left, EVEN = right (p.vii). */
export function middleGroupPosition(box: number, half: 'odd' | 'even'): number {
  const cell = BOARD_B_MIDDLE[String(box)];
  if (!cell) throw new RangeError(`MIDDLE group box must be 1-6, got ${box}`);
  return half === 'odd' ? cell.odd : cell.even;
}

/** BOTTOM stack: "Driver #1 is the highest driver in BOTTOM group, in 20th place." */
export function bottomGroupPosition(driver: number): number {
  if (!Number.isInteger(driver) || driver < 1) {
    throw new RangeError(`BOTTOM group driver index must be >= 1, got ${driver}`);
  }
  return 19 + driver;
}

export interface DriverFinderResult {
  chart: string;
  code: string;
  dice: DiceRecord;
  /** Human-readable slot the chart pointed at, e.g. "TOP group driver #3". */
  slot: string;
  /** The 1-based running-order position that slot corresponds to. */
  targetPosition: number;
  car: TroubleCar | null;
  /** True when the slot was empty and the nearest occupied car was used. */
  substituted: boolean;
  modifiers: Modifier[];
  ruleCitation: string;
}

function nearestOccupied(cars: TroubleCar[], target: number): TroubleCar | null {
  if (cars.length === 0) return null;
  let best = cars[0];
  let bestDist = Math.abs(best.position - target);
  for (const car of cars) {
    const d = Math.abs(car.position - target);
    if (d < bestDist || (d === bestDist && car.position < best.position)) {
      best = car;
      bestDist = d;
    }
  }
  return best;
}

/**
 * Roll the DRIVER FINDER for a group and resolve it against the field.
 *
 * AMBIGUITY: nothing printed says what to do when the chart names a slot no car
 * occupies — a short BOTTOM stack, or a TOP group thinned by an earlier wreck
 * before the gap has been filled. Most defensible reading: use the nearest
 * occupied car in that group (ties go to the car further forward) and record
 * the substitution, rather than re-rolling (a re-roll would silently reweight
 * the chart) or voiding the result (the board offers no "no driver" outcome).
 */
export function rollDriverFinder(
  group: TroubleGroup,
  field: TroubleField,
  dice: Dice,
): DriverFinderResult {
  const chartId =
    group === 'top' ? 'driver-finder-top' : group === 'middle' ? 'driver-finder-middle' : 'driver-finder-bottom';
  const chart = CHARTS[chartId as 'driver-finder-top'];
  const roll = dice.code1166();
  const entry = chart.entries[roll.code];
  if (!entry) throw new RangeError(`${chartId}: no entry for code ${roll.code}`);

  let targetPosition: number;
  let slot: string;
  if (group === 'top') {
    targetPosition = topGroupPosition(entry.driver);
    slot = entry.driver === 'LEADER' ? 'the race LEADER' : `TOP group driver #${entry.driver}`;
  } else if (group === 'middle') {
    const half = entry.half ?? 'odd';
    targetPosition = middleGroupPosition(entry.driver as number, half);
    slot = `MIDDLE group box #${entry.driver} (${half} side)`;
  } else {
    targetPosition = bottomGroupPosition(entry.driver as number);
    slot = `BOTTOM group driver #${entry.driver}`;
  }

  const cars = field[group];
  const exact = cars.find((c) => c.position === targetPosition) ?? null;
  const car = exact ?? nearestOccupied(cars, targetPosition);

  const modifiers: Modifier[] = [
    { name: 'DRIVER FINDER chart', value: chart.name },
    { name: 'code rolled (11-66)', value: roll.code, detail: `red ${roll.red}, white ${roll.white}` },
    { name: 'slot', value: slot },
  ];
  if (group === 'top' && entry.driver === 'LEADER') {
    modifiers.push({ name: 'LEADER counts as TOP group', value: 'yes', detail: CITE.leaderIsTop });
  }
  if (!exact && car) {
    modifiers.push({
      name: 'slot empty — nearest occupied car used',
      value: `${targetPosition} → ${car.position}`,
      detail: 'engine ruling, not printed',
    });
  }

  return {
    chart: chart.name,
    code: roll.code,
    dice: { kind: '11-66', dice: [roll.red, roll.white], code: roll.code, label: 'DRIVER FINDER' },
    slot,
    targetPosition,
    car,
    substituted: Boolean(car) && !exact,
    modifiers,
    ruleCitation: `${CITE.problem}; ${CITE.driverFinder}`,
  };
}

// ---------------------------------------------------------------------------
// PROBLEM
// ---------------------------------------------------------------------------

const PROBLEM_FINDER = CHARTS['problem-finder'];
const ODD_PROBLEM = CHARTS['odd-problem'];

/**
 * A display/telemetry category for the moment tag. NOT a printed rule — the
 * board gives free text only. Keyword-matched, most specific first.
 */
const CATEGORY_PATTERNS: [RegExp, string][] = [
  [/tire|tyre/i, 'tire'],
  [/engine|cylinder|black smoke/i, 'engine'],
  [/cool|overheat|radiator|running hot|fan shuts/i, 'cooling'],
  [/brake/i, 'brake'],
  [/electrical|battery|alternator|ignition|plug wire|tachometer|radio/i, 'electrical'],
  [/clutch|shifter|rear end|carbour|carbur|power steering|just shuts off/i, 'drivetrain'],
  [/handling|loose|tight|vibration|sway bar|shock|suspension|grill|hood/i, 'handling'],
  [/oil pressure|leaking fluid/i, 'fluids'],
  [/pit crew|lug nut|unscheduled pit|pits are open|speeding/i, 'pit'],
  [/driver error|becomes ill|rough driving|aggressive/i, 'driver'],
  [/wreck|spin|smacks wall|bumped|undercarriage/i, 'contact'],
];

export function problemCategory(text: string): string {
  for (const [re, cat] of CATEGORY_PATTERNS) if (re.test(text)) return cat;
  return 'unspecified';
}

export interface ProblemChainStep {
  chart: string;
  code: string;
  text: string;
}

export interface ProblemInput {
  field: TroubleField;
  group: TroubleGroup;
  turn: number;
  totalTurns: number;
  repairAdj?: TrackAdjustment;
  dice: Dice;
  /**
   * Skip the DRIVER FINDER — the problem already has an owner. Used by the
   * red-white-blue TRIPLES rule (p.vi) and the TRIANGLE symbol (p.xvi), where
   * the rulebook names the driver before any chart is consulted.
   */
  driverId?: string;
  cause?: string;
  /** RB/xvi triangle: false skips the Problem Finder and goes straight to repair. */
  specified?: boolean;
  /** The laps-down optional rule is in force — a repaired car loses a lap (D-EZ-12). */
  lapsDown?: boolean;
  /**
   * HOUSE RULE D-EZ-87 — the era dial. 'modern': a FAILED repair on a PROBLEM
   * sends the car to the garage and back out laps down (or to the end of the
   * BOTTOM group without the laps-down rule) instead of out of the race. The
   * printed OUT-of-race rows (lost engine and the rest) are untouched — only a
   * failed repair is forgiven, exactly the scope of the 2025 green triangle
   * (D-EZ-76). Absent = '1990s', the printed game, for direct callers; a race
   * passes 'modern' unless the league turned the dial back.
   */
  era?: Era;
}

export interface ProblemResult extends LedgerEvent {
  eventType: 'problem';
  /** `problem.<category>`, or `problem.averted` when a pit crew escape fired. */
  momentTag: string;
  group: TroubleGroup;
  driverId: string | null;
  position: number | null;
  finder: DriverFinderResult | null;
  /** PROBLEM FINDER first, then ODD PROBLEM if the row redirected. */
  chain: ProblemChainStep[];
  /** The row finally in force. */
  entryText: string;
  category: string;
  averted: boolean;
  pitCrewException: PitCrewException | null;
  repair: RepairResult | null;
  outOfRace: boolean;
  /** Set when a one-race IGNORE OUT ONCE grant struck an OUT off this result. */
  grantUsed?: 'ignoreOutOnce';
  /**
   * D-EZ-76 — the green thumbs-up triangle turned a failed repair into a ruined
   * afternoon instead of a finished one. The caller sets `cannotRace` on him.
   */
  dnfExempted?: true;
  placement: Placement;
  yellowFlag: boolean;
  /** p.iv — "if a YELLOW flag resulted, one YELLOW FLAG turn". */
  yellowTurns: 0 | 1;
  blackFlag: boolean;
  chipsForfeited: number;
  /** Effects this module does not itself apply; the caller applies them. */
  pendingEffects: ChartEffect[];
  /** p.vi — a gap in TOP or MIDDLE must be filled before the turn ends. */
  fillGap: boolean;
}

function findCarById(field: TroubleField, driverId: string): { car: TroubleCar; group: TroubleGroup } | null {
  for (const group of ['top', 'middle', 'bottom'] as TroubleGroup[]) {
    const car = field[group].find((c) => c.driverId === driverId);
    if (car) return { car, group };
  }
  return null;
}

function exceptionApplies(exception: PitCrewException | null | undefined, grade: PitCrewGrade): boolean {
  return Boolean(exception && exception.outcome === 'noEffect' && exception.grades.includes(grade));
}

/**
 * Resolve a PROBLEM result: DRIVER FINDER, then PROBLEM FINDER, then the row.
 */
export function resolveProblem(input: ProblemInput): ProblemResult {
  const { field, dice } = input;

  // --- 1. Who has the problem? (p.iv — DRIVER FINDER first) ------------------
  let finder: DriverFinderResult | null = null;
  let car: TroubleCar | null = null;
  let group = input.group;

  if (input.driverId) {
    const found = findCarById(field, input.driverId);
    if (!found) throw new RangeError(`no car with driverId "${input.driverId}" in the field`);
    car = found.car;
    group = found.group;
  } else {
    finder = rollDriverFinder(input.group, field, dice);
    car = finder.car;
  }

  const diceRecords: DiceRecord[] = finder ? [finder.dice] : [];
  const modifiers: Modifier[] = finder ? [...finder.modifiers] : [{ name: 'driver named directly', value: input.driverId ?? '' }];

  if (!car) {
    // The indicated group is empty. Nothing to resolve.
    return {
      eventType: 'problem',
      momentTag: 'problem.no-driver',
      ruleCitation: CITE.problem,
      actorIds: [],
      dice: diceRecords,
      modifiers,
      description: `A PROBLEM was called for the ${group.toUpperCase()} group, but there are no cars in it. No effect.`,
      positionDeltas: [],
      group,
      driverId: null,
      position: null,
      finder,
      chain: [],
      entryText: '',
      category: 'none',
      averted: true,
      pitCrewException: null,
      repair: null,
      outOfRace: false,
      placement: 'hold',
      yellowFlag: false,
      yellowTurns: 0,
      blackFlag: false,
      chipsForfeited: 0,
      pendingEffects: [],
      fillGap: false,
    };
  }

  const who = car.name ?? car.driverId;

  // --- 2. What is the problem? (PROBLEM FINDER) -----------------------------
  const pfRoll = input.specified === false ? null : dice.code1166();
  const pfEntry: ChartEntry | undefined = pfRoll ? PROBLEM_FINDER.entries[pfRoll.code] : {
    text: 'Unspecified PROBLEM — conduct a repair roll (RB/xvi).',
    effects: [{ type: 'rollForRepair', repairTable: 'problem' }],
  };
  if (!pfEntry) throw new RangeError(`problem-finder: no entry for code ${pfRoll?.code}`);
  if (pfRoll) {
    diceRecords.push({ kind: '11-66', dice: [pfRoll.red, pfRoll.white], code: pfRoll.code, label: 'PROBLEM FINDER' });
    modifiers.push({ name: 'PROBLEM FINDER row', value: pfRoll.code, detail: pfEntry.text });
  } else {
    modifiers.push({ name: 'triangle unspecified problem', value: 'direct repair', detail: 'RB/xvi — no Problem Finder roll' });
  }
  const problemReading = pfRoll ? `${pfRoll.code} on the PROBLEM FINDER: "${pfEntry.text}"` : pfEntry.text;
  const chain: ProblemChainStep[] = pfRoll ? [{ chart: PROBLEM_FINDER.name, code: pfRoll.code, text: pfEntry.text }] : [];

  // --- 3. Pit crew escape, BEFORE anything else ------------------------------
  if (exceptionApplies(pfEntry.pitCrewException, car.pitCrewGrade)) {
    const ex = pfEntry.pitCrewException as PitCrewException;
    modifiers.push({
      name: 'pit crew exception',
      value: `Pit Crew ${ex.grades.join(' or ')}`,
      detail: 'printed on the row — the problem does not occur',
    });
    return {
      eventType: 'problem',
      momentTag: 'problem.averted',
      ruleCitation: `${CITE.problem}; ${PROBLEM_FINDER.sourceCitation}`,
      actorIds: [car.driverId],
      dice: diceRecords,
      modifiers,
      description:
        `${problemReading} — but ${who}'s grade-${car.pitCrewGrade} ` +
        `crew is covered by the printed exception, so the problem does not occur. No change.`,
      positionDeltas: [{ driverId: car.driverId, from: car.position, to: car.position, placement: 'hold' }],
      group,
      driverId: car.driverId,
      position: car.position,
      finder,
      chain,
      entryText: pfEntry.text,
      category: problemCategory(pfEntry.text),
      averted: true,
      pitCrewException: ex,
      repair: null,
      outOfRace: false,
      placement: 'hold',
      yellowFlag: false,
      yellowTurns: 0,
      blackFlag: false,
      chipsForfeited: 0,
      pendingEffects: [],
      fillGap: false,
    };
  }

  // --- 3b. EZRA ENGINEER trait ----------------------------------------------
  // "Any time this driver is faced with a problem roll a d6. 1-3 the problem
  //  exists handle per normal rules, 4-6 the problem is avoided."
  //
  // RULING (D-EZ-5): resolved AFTER the printed pit-crew exception and BEFORE
  // the ODD PROBLEM redirect. The printed exception costs nothing and is on the
  // row itself, so it is checked first and no die is burned when it already
  // covers the car; and rolling before the redirect means the Engineer never
  // reads a chart he was going to sidestep anyway, which keeps the dice log
  // matching what a human would actually have rolled at the table.
  if (car.trait === 'EN') {
    const en = dice.d6();
    diceRecords.push({ kind: 'd6', dice: [en], total: en, label: 'ENGINEER (EN trait)' });
    modifiers.push({ name: 'EN trait — Engineer', value: `${en}`, detail: CITE_EN_TRAIT });
    if (en >= EN_AVOIDS_FROM) {
      return {
        eventType: 'problem',
        momentTag: 'problem.averted',
        ruleCitation: `${CITE.problem}; ${CITE_EN_TRAIT}`,
        actorIds: [car.driverId],
        dice: diceRecords,
        modifiers,
        description:
          `${problemReading} — but ${who} saw it building, ` +
          `worked around it in the cockpit and lost nothing. Engineer, and a ${en} on the die.`,
        positionDeltas: [{ driverId: car.driverId, from: car.position, to: car.position, placement: 'hold' }],
        group,
        driverId: car.driverId,
        position: car.position,
        finder,
        chain,
        entryText: pfEntry.text,
        category: problemCategory(pfEntry.text),
        averted: true,
        pitCrewException: null,
        repair: null,
        outOfRace: false,
        placement: 'hold',
        yellowFlag: false,
        yellowTurns: 0,
        blackFlag: false,
        chipsForfeited: 0,
        pendingEffects: [],
        fillGap: false,
      };
    }
  }

  // --- 4. ODD PROBLEM redirect ----------------------------------------------
  let entry = pfEntry;
  let sourceChart = pfRoll ? PROBLEM_FINDER.name : 'RB/xvi — triangle unspecified problem';
  const redirect = (pfEntry.effects ?? []).find((e) => e.type === 'rerollOn' && e.chart === 'odd-problem');
  if (redirect) {
    const opRoll = dice.code1166();
    const opEntry = ODD_PROBLEM.entries[opRoll.code];
    if (!opEntry) throw new RangeError(`odd-problem: no entry for code ${opRoll.code}`);
    diceRecords.push({ kind: '11-66', dice: [opRoll.red, opRoll.white], code: opRoll.code, label: 'ODD PROBLEM' });
    modifiers.push({ name: 'ODD PROBLEM row', value: opRoll.code, detail: opEntry.text });
    chain.push({ chart: ODD_PROBLEM.name, code: opRoll.code, text: opEntry.text });
    entry = opEntry;
    sourceChart = ODD_PROBLEM.name;
  }

  // --- 5. Apply the row's effects -------------------------------------------
  let repair: RepairResult | null = null;
  let outOfRace = false;
  let placement: Placement = 'hold';
  let yellowFlag = false;
  let blackFlag = false;
  let chipsForfeited = 0;
  const pendingEffects: ChartEffect[] = [];
  const alreadyInBottom = groupOf(car.position) === 'bottom';
  /**
   * POST RACE INTERVIEW — "IGNORE OUT ONCE- Ignore any OUT result (one time)
   * from the Problem or Feud/Fume chart." (D-EZ-15, one race.)
   *
   * The row still resolves in full: the dice are rolled, the chart is read, the
   * yellow still comes out if the row waves one, and the description still says
   * what happened. Only the OUT is struck. That is what "ignore any OUT result"
   * says — it names the result, not the event.
   */
  let ignoredOut = false;
  /** D-EZ-76 — the green triangle saved him from a failed repair. */
  let dnfExempted = false;
  /** D-EZ-87 — modern era: the failed repair became a trip to the garage. */
  let garageRepair = false;

  for (const effect of entry.effects ?? []) {
    switch (effect.type) {
      case 'rerollOn':
        // Already handled above; odd-problem has no further redirects.
        break;
      case 'rollForRepair': {
        const table = (effect.repairTable as RepairTable) ?? 'problem';
        repair = resolveRepair({
          car: {
            driverId: car.driverId,
            name: car.name,
            pitCrewGrade: car.pitCrewGrade,
            position: car.position,
            chips: car.chips ?? 0,
          },
          table,
          turn: input.turn,
          totalTurns: input.totalTurns,
          repairAdj: input.repairAdj,
          dice,
          cause: entry.text,
          lapsDown: input.lapsDown,
        });
        diceRecords.push(...repair.dice);
        modifiers.push(...repair.modifiers);
        if (repair.outOfRace && car.ignoreOutOnce && !ignoredOut) {
          ignoredOut = true;
          placement = input.lapsDown ? 'laps-down' : 'end-of-bottom';
        } else if (repair.outOfRace && car.dnfExempt) {
          /*
            THE GREEN THUMBS-UP TRIANGLE — D-EZ-76, and it applies HERE and
            nowhere else.

            The 2025 set: *"if the pit crew fails the REPAIR roll for a PROBLEM
            result, rather than being knocked out of the race, they instead go to
            the top (last place) of the BOTTOM group stack, but lose their
            ability to challenge or defend for the rest of the race."*

            ⚠ IT IS NOT A GENERAL IMMUNITY, and the same paragraph says so:
            *"NOTE that it is still possible for these drivers to be knocked out
            of a race on PROBLEM rolls of 13, 21 or 66, which are 'out of race'
            results."* Those rows come through the `outOfRace` case below, which
            this branch deliberately does not touch — only a failed REPAIR is
            forgiven.

            Unlike `ignoreOutOnce` there is no "once": the triangle is printed on
            the card and holds all season. What it costs is the rest of his
            afternoon as a racer, which `cannotRace` carries.
          */
          dnfExempted = true;
          placement = input.lapsDown ? 'laps-down' : 'end-of-bottom';
        } else if (repair.outOfRace && input.era === 'modern') {
          // HOUSE RULE D-EZ-87 — modern machinery: the crew cannot fix it on pit
          // road, so the car goes behind the wall and comes back laps down.
          garageRepair = true;
          placement = input.lapsDown ? 'laps-down' : 'end-of-bottom';
        } else {
          outOfRace = outOfRace || repair.outOfRace;
          placement = repair.repaired
            ? (input.lapsDown ? 'laps-down' : 'end-of-bottom')
            : 'out-of-race';
        }
        break;
      }
      case 'outOfRace':
        if (car.ignoreOutOnce && !ignoredOut) {
          ignoredOut = true;
          // He keeps going, but he does not keep his place — the incident still
          // happened. End of the BOTTOM group is the board's own default for a
          // car that survived something.
          placement = input.lapsDown ? 'laps-down' : 'end-of-bottom';
        } else {
          outOfRace = true;
          placement = 'out-of-race';
        }
        break;
      case 'moveToEndOfBottom':
        if (!outOfRace) placement = 'end-of-bottom';
        break;
      case 'moveToFrontOfBottom':
        // AMBIGUITY (charts.json needsReview NR-001): the printed footnote
        // "*Driver already in BOTTOM group moved to end of group, not front."
        // carries an asterisk on rows 24/34/36/41/51 but NOT on row 23, whose
        // wording is identical. Applied uniformly here: moving a car that is
        // already 35th to the FRONT of the BOTTOM group would PROMOTE it to
        // 20th as a punishment, which cannot be the intent on any row.
        if (!outOfRace) placement = alreadyInBottom ? 'end-of-bottom' : 'front-of-bottom';
        break;
      case 'yellowFlag':
        yellowFlag = true;
        break;
      case 'blackFlag':
        // The BLACK FLAG table's own rule line: "Black-flagged driver sent to
        // end of BOTTOM Group." The ODD PROBLEM row that raises a black flag
        // (14) does not say to roll for the infraction, so no d6 is rolled.
        blackFlag = true;
        if (!outOfRace) placement = 'end-of-bottom';
        break;
      case 'loseAllPerformanceChips':
        chipsForfeited = Math.max(chipsForfeited, car.chips ?? 0);
        break;
      default:
        pendingEffects.push(effect);
        break;
    }
  }

  // p.xv — chips cannot be carried in the BOTTOM group, and a PROBLEM can never
  // be bought off (p.xiv), so any chip in hand is forfeited on the way back.
  if (placement !== 'hold') {
    chipsForfeited = Math.max(chipsForfeited, car.chips ?? 0);
  }

  const category = problemCategory(entry.text);
  const positionDeltas: PositionDelta[] = [
    { driverId: car.driverId, from: car.position, to: placement === 'hold' ? car.position : null, placement },
  ];

  const fillGap = placement !== 'hold' && groupOf(car.position) !== 'bottom';

  modifiers.push({ name: 'pit crew grade', value: car.pitCrewGrade });
  if (yellowFlag) modifiers.push({ name: 'YELLOW flag', value: 1, detail: 'RB/iv — one YELLOW FLAG turn' });
  if (blackFlag) modifiers.push({ name: 'BLACK flag', value: 'end of BOTTOM group' });
  if (chipsForfeited > 0) modifiers.push({ name: 'performance chips forfeited', value: chipsForfeited, detail: CITE.chipsBottom });

  const outcomeSentence = outOfRace
    ? `${who} is out of the race.`
    : garageRepair
      ? `The crew cannot fix it on pit road — ${who} goes behind the wall and comes back out ${input.lapsDown ? 'laps down' : 'at the back'}.`
    : placement === 'end-of-bottom'
      ? `${who} goes to the END of the BOTTOM group.`
      : placement === 'front-of-bottom'
        ? `${who} drops to the FRONT of the BOTTOM group.`
        : `${who} carries on.`;

  return {
    eventType: 'problem',
    momentTag: `problem.${category}`,
    ruleCitation: `${CITE.problem}; ${sourceChart} (${PROBLEM_FINDER.sourceCitation})`,
    actorIds: [car.driverId],
    dice: diceRecords,
    modifiers: garageRepair
      ? [...modifiers, { name: 'era', value: 'modern', detail: CITE_ERA }]
      : ignoredOut
      ? [
          ...modifiers,
          {
            name: 'interview grant — IGNORE OUT ONCE',
            value: car.driverId,
            detail:
              'RWBR_postraceinterview.pdf — "Ignore any OUT result (one time) from the Problem or ' +
              'Feud/Fume chart." The row still resolved in full; only the OUT was struck.',
          },
        ]
      : modifiers,
    description:
      `${input.cause ? `${input.cause}: ` : ''}${chain.length ? chain.map((s) => `${s.code} "${s.text}"`).join(' → ') : entry.text}. ` +
      `${outcomeSentence}${yellowFlag ? ' The yellow comes out for one turn.' : ''}`,
    positionDeltas,
    group,
    driverId: car.driverId,
    position: car.position,
    finder,
    chain,
    entryText: entry.text,
    category,
    averted: false,
    pitCrewException: (pfEntry.pitCrewException as PitCrewException | null) ?? null,
    repair,
    outOfRace,
    ...(ignoredOut ? { grantUsed: 'ignoreOutOnce' as const } : {}),
    ...(dnfExempted ? { dnfExempted: true as const } : {}),
    placement,
    yellowFlag,
    yellowTurns: yellowFlag ? 1 : 0,
    blackFlag,
    chipsForfeited,
    pendingEffects,
    fillGap,
  };
}
